/* eslint-disable no-empty */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Inject,
  Injector,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  ApiClientInterface,
  RouterFacadeInterface,
  ROUTER_SERVICE_TOKEN,
} from '@ideapa/app-common';
import { BaseDto } from '@ideapa/common-dto';
import {
  FieldType,
  FilterOperator,
  FilterType,
  FilterValue,
} from '@ideapa/common-interfaces';
import { camelize, getDescendantProp } from '@ideapa/common-utils';
import { NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap/typeahead';
import moment from 'moment';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  OperatorFunction,
  switchMap,
  tap,
} from 'rxjs';
import { BaseFormFieldComponent } from '../base-form-field.component';

/**
 * Interfaccia che rappresenta la configurazione del campo Autocomplete.
 */
export interface AutocompleteConfig {
  entity: string;
  decodeField: string[];
  decodeFieldDisabled?: boolean;
  decodeFieldFormat?: FieldType[];
  codeField?: string;
  filters?: FilterValue[];
  join?: string[];
  tooltip?: string;
  /**
   * Specifica se il server deve applicare o meno i filtri utente di dominio
   * ai risultati di ricerca (es: filtro per ente).
   *
   * Se true, il server non applica i filtri ai risultati di ricerca.
   *
   * Se false o undefined, il server applica i filtri ai risultati di ricerca.
   *
   * @values true, false, undefined
   *
   * Usato: Moduli 'compagine-sociale'.
   */
  areDomainUserFiltersDisabled?: boolean;
  /**
   * Specifica se il server deve restituire tutte le entità come valide per la
   * selezione o meno.
   *
   * Se true, il server blocca le entità in base a logiche di dominio,
   * permettendone solo la visualizzazione.
   *
   * Se false o undefined, il server restituisce tutte le entità
   * come selezionabili.
   *
   * @values true, false, undefined
   *
   * Usato: Moduli 'scrittura-elisione', 'scrittura-preconsolidamento'.
   */
  isEntitySelectionLimited?: boolean;
  /**
   * Specifica se il server deve ignorare il filtro per Ente e Anno
   * durante il recupero dei risultati di ricerca.
   *
   * Se true, il server non applica i filtri ai risultati di ricerca.
   *
   * Se false o undefined, il server applica i filtri ai risultati di ricerca.
   *
   * @values true, false, undefined
   *
   * Usato: Moduli 'utente-partecipata'.
   */
  areEnteAnnoFiltersDisabled?: boolean;
}

@Component({
  standalone: false,
  selector: 'ideapa-autocomplete-field',
  styleUrls: ['./autocomplete-field.component.scss'],
  template: `
    <ideapa-form-field-wrapper
      [label]="label"
      [shouldShowError]="shouldShowError"
      [errors]="errors"
      [tooltip]="config.tooltip || ''"
      >
      <div class="d-flex align-items-center">
        <div
          class="position-relative code me-1"
          [hidden]="!config.codeField"
          [class.big]="bigInputCode"
          >
          <input
            #codeInput
            type="text"
            class="form-control"
            [class.is-invalid]="shouldShowError"
            [formControl]="formControl"
            [ngbTypeahead]="searchByCode"
            [inputFormatter]="codeFormatter"
            [resultFormatter]="resultFormatter"
            [showHint]="false"
            [editable]="false"
            (selectItem)="selected($event)"
            (blur)="blur(codeInput, decodeInput)"
            [readonly]="readOnly"
            [container]="'body'"
            (keydown.f4)="search()"
            />
          @if (isLoading) {
            <div
              class="spinner-border spinner-border-sm text-primary"
              role="status"
              >
              <span class="visually-hidden">Caricamento...</span>
            </div>
          }
        </div>
        <div class="position-relative decode">
          <input
            #decodeInput
            type="text"
            class="form-control "
            [class.is-invalid]="shouldShowError"
            [formControl]="formControl"
            [ngbTypeahead]="searchByDecode"
            [inputFormatter]="decodeFormatter"
            [resultFormatter]="resultFormatter"
            [showHint]="false"
            [editable]="decodeFieldDisabled()"
            (selectItem)="selected($event)"
            (blur)="blur(codeInput, decodeInput)"
            [readonly]="decodeFieldDisabled() || readOnly"
            [container]="'body'"
            (keydown.f4)="search()"
            />
          @if (isLoading) {
            <div
              class="spinner-border spinner-border-sm text-primary"
              role="status"
              >
              <span class="visually-hidden">Caricamento...</span>
            </div>
          }
        </div>
        <ng-container>
          @if (
            canView && !formControl.disabled && !readOnly && allowFullSearch
            ) {
            <ideapa-icon-button
              class="ms-1"
              cssClasses="text-primary"
              icon="search"
              title="ricerca estesa"
              tabindex="-1"
              (clicked)="search()"
              >
            </ideapa-icon-button>
          }
          @if (canView && formControl.value) {
            <ideapa-icon-button
              class="ms-1"
              cssClasses="text-primary"
              icon="visibility"
              title="visualizza"
              tabindex="-1"
              (clicked)="view()"
              >
            </ideapa-icon-button>
          }
          @if (canAdd && !formControl.disabled && !readOnly) {
            <ideapa-icon-button
              class="ms-1"
              cssClasses="text-primary"
              icon="note_add"
              title="crea nuovo"
              tabindex="-1"
              (clicked)="add()"
              >
            </ideapa-icon-button>
          }
        </ng-container>
      </div>
    </ideapa-form-field-wrapper>
    `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteFieldComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutocompleteFieldComponent
  extends BaseFormFieldComponent
  implements ControlValueAccessor, OnInit, AfterViewInit
{
  @ViewChild('codeInput', { static: true })
  codeInput: ElementRef<HTMLInputElement> = {} as ElementRef;

  @ViewChild('decodeInput', { static: true })
  decodeInput: ElementRef<HTMLInputElement> = {} as ElementRef;

  @Input()
  placeholder = '';

  @Input()
  config!: AutocompleteConfig;

  // @Input()
  // bindValue = 'id';

  @Input()
  canAdd? = false;

  @Input()
  canView? = false;

  @Input()
  allowFullSearch? = true;

  @Input()
  exactMatch = false;

  @Input()
  startSearchAfterInsertedChars = 2;

  isLoading = false;

  @Input()
  bigInputCode = false;

  momentDate = moment;

  @Input()
  setFocusOnInit = false;

  @Output()
  selectedItem = new EventEmitter<any>();
  // decodeFormControl = new FormControl();

  debounceTimeMicroseconds = 300;
  itemsPerPage = 45;

  get apiClient() {
    const entityName = camelize(this.config.entity);
    return this.injector.get(
      entityName + 'ApiClient'
    ) as ApiClientInterface<BaseDto>;
  }

  decodeFieldDisabled(): boolean {
    if (this.config.decodeFieldDisabled === true) {
      return true;
    }
    return false;
  }

  // TODO: - quali FieldType consideriamo?
  getFormattedValue(value?: any, type?: FieldType): any {
    switch (type) {
      case FieldType.date:
        return value != null ? this.momentDate(value).format('DD/MM/YYYY') : '';
      case FieldType.time:
        return value != null ? this.momentDate(value).format('HH:mm:ss') : '';
      case FieldType.datetime:
        return value != null
          ? this.momentDate(value).format('DD/MM/YYYY HH:mm:ss')
          : '';
    }
    return value;
  }

  // moment(arg0: any) {
  //   throw new Error('Method not implemented.');
  // }

  codeFormatter = (x: any) => {
    if (this.config.codeField)
      return getDescendantProp(x, this.config.codeField);
  };

  decodeFormatter = (x: any) => {
    let value = '';
    this.config.decodeField.map((dec, index) => {
      let type: FieldType = FieldType.text;
      if (this.config.decodeFieldFormat != undefined) {
        type = this.config.decodeFieldFormat[index];
      }
      value +=
        (value == '' ? '' : ' ') +
        this.getFormattedValue(getDescendantProp(x, dec), type);
    });
    return value;
  };

  resultFormatter = (x: any) => {
    let result = '';

    this.config.decodeField.map((dec, index) => {
      let type: FieldType = FieldType.text;
      if (this.config.decodeFieldFormat != undefined) {
        type = this.config.decodeFieldFormat[index];
      }
      result +=
        (result == '' ? '' : ' ') +
        this.getFormattedValue(getDescendantProp(x, dec), type);
    });

    if (this.config.codeField) {
      return `${x[this.config.codeField]} - ${result}`;
    } else {
      return `${result}`;
    }
  };

  blur(codeInput: HTMLInputElement, decodeInput: HTMLInputElement) {
    if (!this.formControl.value) {
      codeInput.value = decodeInput.value = '';
      this.isLoading = false;
      this.cd.detectChanges();
    }
  }

  selected($event: NgbTypeaheadSelectItemEvent) {
    this.formControl.setValue($event.item);
    this.selectedItem.emit($event.item);
  }

  searchByCode: OperatorFunction<string, readonly any[]> = (
    text$: Observable<string>
  ) => {
    if (!this.config.codeField) return of([]);

    const codeField = this.config.codeField;
    return text$.pipe(
      debounceTime(this.debounceTimeMicroseconds),
      distinctUntilChanged(),
      filter((term) => term.length >= this.startSearchAfterInsertedChars),
      tap(() => {
        this.isLoading = true;
        this.cd.detectChanges();
      }),
      switchMap((term) => {
        // const join: string[] = [];
        const allFilters: FilterValue[] = [
          {
            field: codeField,
            operator: this.exactMatch
              ? FilterOperator.equals
              : FilterOperator.startsWith,
            value: [term],
            type: FilterType.text,
          },
        ];
        if (this.config.filters) {
          this.config.filters.map((f) => {
            // if (f.field.indexOf('.') > 0) {
            //   const splitted = f.field.split('.');
            //   if (splitted.length > 1) {
            //     join.push(splitted[0]);
            //   }
            // }
            allFilters.push(f);
          });
        }
        return this.apiClient
          .getPaged(
            this.itemsPerPage,
            0,
            {
              [codeField]: 'asc',
            },
            allFilters,
            // join
            this.config.join,
            this.config.areDomainUserFiltersDisabled,
            this.config.isEntitySelectionLimited,
            this.config.areEnteAnnoFiltersDisabled
          )
          .pipe(
            map((res) => res.items),
            catchError(() => of([])), // empty list on error
            tap(() => {
              this.isLoading = false;
              this.cd.detectChanges();
            })
          );
      })
    );
  };

  searchByDecode: OperatorFunction<string, readonly any[]> = (
    text$: Observable<string>
  ) =>
    text$.pipe(
      debounceTime(this.debounceTimeMicroseconds),
      distinctUntilChanged(),
      filter((term) => {
        return term.length >= this.startSearchAfterInsertedChars;
      }),
      tap(() => {
        this.isLoading = true;
        this.cd.detectChanges();
      }),
      switchMap((term) => {
        let sortBy: any = {};
        // const join: string[] = [];
        // sort multiplo
        // filtro su più campi con OR
        const allFilters: FilterValue[] = this.config.decodeField.map((f) => {
          sortBy = Object.assign(sortBy, {
            [f]: 'asc',
          });
          return {
            field: f,
            operator: this.exactMatch
              ? FilterOperator.equals
              : FilterOperator.contains,
            value: [term],
            type: FilterType.text,
            bracketCode: 'multi',
          };
        });

        // considero solo il primo campo
        // const allFilters: FilterValue[] = [
        //   {
        //     field: this.config.decodeField[0],
        //     operator: FilterOperator.contains,
        //     value: [term],
        //     type: FilterType.text,
        //   },
        // ];
        // sortBy = Object.assign(sortBy, {
        //    [this.config.decodeField[0]]: 'asc',
        // })

        if (this.config.filters) {
          this.config.filters.map((f) => {
            // if (f.field.indexOf('.') > 0) {
            //   const splitted = f.field.split('.');
            //   if (splitted.length > 1) {
            //     join.push(splitted[0]);
            //   }
            // }
            allFilters.push(f);
          });
        }
        return this.apiClient
          .getPaged(
            this.itemsPerPage,
            0,
            sortBy,
            allFilters,
            // join,
            this.config.join,
            this.config.areDomainUserFiltersDisabled,
            this.config.isEntitySelectionLimited,
            this.config.areEnteAnnoFiltersDisabled
          )
          .pipe(
            map((res) => res.items),
            catchError(() => of([])), // empty list on error
            tap(() => {
              this.isLoading = false;
              this.cd.detectChanges();
            })
          );
      })
    );

  async add() {
    if (this.config.entity) {
      try {
        const addedItem = await this.routerFacade.addNewEntity(
          this.config.entity
        );
        this.formControl.setValue(addedItem);
        this.cd.detectChanges();
      } catch (error) {}
    }
  }

  async search() {
    if (this.config.entity) {
      try {
        const foundItem = await this.routerFacade.searchEntity(
          this.config.entity,
          this.config.filters,
          this.config.areDomainUserFiltersDisabled,
          this.config.isEntitySelectionLimited,
          this.config.areEnteAnnoFiltersDisabled
        );
        this.formControl.setValue(foundItem);

        this.selectedItem.emit(foundItem);

        this.cd.detectChanges();

        window.setTimeout(() => {
          this.decodeInput?.nativeElement.focus();
        });
      } catch (error) {}
    }
  }

  async view() {
    if (this.config.entity) {
      this.routerFacade.openEntityDetail(
        this.config.entity,
        this.formControl.value.id,
        false
      );
    }
  }

  constructor(
    private cd: ChangeDetectorRef,
    @Inject(ROUTER_SERVICE_TOKEN)
    private routerFacade: RouterFacadeInterface,
    private injector: Injector
  ) {
    super();
  }

  ngAfterViewInit(): void {
    // console.log(
    //   `AutocompleteFieldComponent setFocusOnInit: ${this.setFocusOnInit}`
    // );
    if (this.setFocusOnInit) this.decodeInput?.nativeElement.focus();
  }
}
